import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';

interface Project {
  id: number;
  title: string;
  type: string;
  duration: string;
  status: '已完成' | '编辑中' | '草稿';
  lastEdited: string;
  thumbnail: string;
}

const ProjectsPage: React.FC = () => {
  const [filter, setFilter] = useState('全部');

  const projects: Project[] = [
    {
      id: 1,
      title: '都市咖啡爱情故事',
      type: '现代都市',
      duration: '3分钟',
      status: '已完成',
      lastEdited: '2小时前',
      thumbnail: '☕'
    },
    {
      id: 2,
      title: '校园青春回忆',
      type: '青春校园',
      duration: '5分钟',
      status: '编辑中',
      lastEdited: '1天前',
      thumbnail: '🎓'
    },
    {
      id: 3,
      title: '古装仙侠传',
      type: '古装言情',
      duration: '10分钟',
      status: '草稿',
      lastEdited: '3天前',
      thumbnail: '🗡️'
    }
  ];

  const filteredProjects = filter === '全部'
    ? projects
    : projects.filter(project => project.status === filter);

  const getStatusColor = (status: string) => {
    switch (status) {
      case '已完成': return 'bg-green-100 text-green-800';
      case '编辑中': return 'bg-blue-100 text-blue-800';
      case '草稿': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 面包屑导航 */}
        <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-8">
          <Link to="/" className="hover:text-purple-600">首页</Link>
          <span>/</span>
          <span className="text-gray-900">项目管理</span>
        </nav>

        {/* 页面标题 */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">我的项目</h1>
            <p className="text-gray-600">管理您的所有短剧创作项目</p>
          </div>
          <Link
            to="/create"
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-800 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-purple-900 transition-all duration-200"
          >
            + 新建项目
          </Link>
        </div>

        {/* 统计卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">总项目数</p>
                <p className="text-2xl font-bold text-gray-900">12</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <span className="text-purple-600 text-xl">📁</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">进行中</p>
                <p className="text-2xl font-bold text-gray-900">3</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <span className="text-blue-600 text-xl">⚡</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">已完成</p>
                <p className="text-2xl font-bold text-gray-900">7</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <span className="text-green-600 text-xl">✅</span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">本月创作</p>
                <p className="text-2xl font-bold text-gray-900">5</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <span className="text-orange-600 text-xl">📈</span>
              </div>
            </div>
          </div>
        </div>

        {/* 项目筛选 */}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex space-x-4">
              <button
                onClick={() => setFilter('全部')}
                className={`px-4 py-2 rounded-lg ${filter === '全部' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                全部
              </button>
              <button
                onClick={() => setFilter('编辑中')}
                className={`px-4 py-2 rounded-lg ${filter === '编辑中' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                进行中
              </button>
              <button
                onClick={() => setFilter('已完成')}
                className={`px-4 py-2 rounded-lg ${filter === '已完成' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                已完成
              </button>
              <button
                onClick={() => setFilter('草稿')}
                className={`px-4 py-2 rounded-lg ${filter === '草稿' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                草稿
              </button>
            </div>
            <div className="flex items-center space-x-4">
              <input
                type="text"
                placeholder="搜索项目..."
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                <option>按时间排序</option>
                <option>按名称排序</option>
                <option>按状态排序</option>
              </select>
            </div>
          </div>
        </div>

        {/* 项目列表 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => (
            <div key={project.id} className="bg-white rounded-xl shadow-sm border hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center text-2xl">
                      {project.thumbnail}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{project.title}</h3>
                      <p className="text-sm text-gray-600">{project.type} • {project.duration}</p>
                    </div>
                  </div>
                  <span
                    onClick={() => {
                      const message = `项目状态: ${project.status}\n\n`;
                      const statusDetails = {
                        '已完成': '✅ 项目已完成所有制作流程，可以进行发布或分享',
                        '编辑中': '⚡ 项目正在编辑中，可以继续完善剧本和分镜',
                        '草稿': '📝 项目处于草稿状态，可以开始或继续创作'
                      };
                      alert(message + statusDetails[project.status]);
                    }}
                    className={`px-2 py-1 text-xs font-medium rounded-full cursor-pointer hover:opacity-80 ${getStatusColor(project.status)}`}
                  >
                    {project.status}
                  </span>
                </div>

                <div className="text-sm text-gray-500 mb-4">
                  最后编辑：{project.lastEdited}
                </div>

                <div className="flex space-x-2">
                  <Link
                    to={`/create`}
                    className="flex-1 px-3 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors text-center"
                  >
                    继续编辑
                  </Link>
                  <button
                    onClick={() => {
                      const menuOptions = [
                        '📋 查看项目详情',
                        '📤 导出项目',
                        '📝 重命名项目',
                        '🗑️ 删除项目',
                        '📊 项目统计'
                      ];

                      const selectedOption = prompt(`选择操作:\n${menuOptions.map((opt, index) => `${index + 1}. ${opt}`).join('\n')}\n\n请输入选项编号 (1-5):`);

                      if (selectedOption) {
                        const optionNum = parseInt(selectedOption);
                        if (optionNum >= 1 && optionNum <= 5) {
                          alert(`您选择了: ${menuOptions[optionNum - 1]}\n\n功能开发中，敬请期待！`);
                        }
                      }
                    }}
                    className="px-3 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    ⋮
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 空状态提示 */}
        <div className="mt-12 text-center">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">📚</span>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">创作更多精彩内容</h3>
          <p className="text-gray-600 mb-4">
            使用AI助手，让您的创意得到完美呈现
          </p>
          <Link
            to="/create"
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-800 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-purple-900 transition-all duration-200"
          >
            开始新的创作
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProjectsPage;